import { Box, useMantineTheme } from '@mantine/core';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import SingleSelectorTarget from '@repo/common/components/SingleSelector/SingleSelectorTarget';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { useMemo } from 'react';
import type { IBaseModel } from '../../api';

interface OptimizedSelectorRenderProps<T> {
	record: T;
	accessor: string;
	esAccessor?: string;
	nilOption: string | boolean;
	hideOnEmpty?: boolean;
}

/**
 * Lightweight read-only component to display selector values without selector functionality.
 * Used when user doesn't have permission to edit the field.
 * Directly renders SingleSelectorTarget for better performance.
 */
export function OptimizedSelectorRender<T extends IBaseModel>({
	record,
	accessor,
	esAccessor,
	nilOption,
	hideOnEmpty = true,
}: OptimizedSelectorRenderProps<T>) {
	const theme = useMantineTheme();

	const selectedItem = useMemo(() => {
		const value = (record?.[accessor as keyof T] ??
			record?.[esAccessor as keyof T] ??
			nilOption) as string | boolean;

		const options = SELECTABLE_PROPERTY_OPTIONS[accessor] || [];
		return options.find(
			(option: SelectablePropertyItem) => option.value === value
		);
	}, [record, accessor, esAccessor, nilOption]);

	return (
		<Box ml={`-${theme.spacing['2xs']}`}>
			<SingleSelectorTarget
				selected={selectedItem}
				placeholder={`No ${accessor}`}
				hideOnEmpty={hideOnEmpty}
				readOnly
				iconType="tabler"
				isViewerUser
			/>
		</Box>
	);
}
